import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Is this seller's Compliance Profile complete?
//
// Manufacturer and packer name and address are legally required on an Indian
// marketplace listing. They are account-level facts — the same on every product
// this seller ever creates — so they are asked once in Settings and auto-filled
// everywhere after that.
//
// The consequence of them being missing is not cosmetic: the export writes
// blank cells into the marketplace template and the upload is rejected at
// quality check, days later, with no obvious cause. So this is a gate before
// generation, not a suggestion afterwards.
//
// One shared fetch: the banner, the reminder and the generate button all need
// the same answer, and three components each asking separately would be three
// requests that can disagree with each other.
// ---------------------------------------------------------------------------

export const ComplianceContext = createContext({
  complete: true, profile: null, loading: true, missing: [], refresh: () => {}
});

// The four the server checks. Brand name and the importer block are genuinely
// optional — an importer only applies to imported goods.
const REQUIRED = [
  ['manufacturerName', 'Manufacturer name'],
  ['manufacturerAddress', 'Manufacturer address'],
  ['packerName', 'Packer name'],
  ['packerAddress', 'Packer address']
];

export function ComplianceProvider({ children }) {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [state, setState] = useState({ complete: true, profile: null, loading: true, missing: [] });

  const refresh = useCallback(async () => {
    // Vendors and admins never publish a seller listing, so a compliance
    // profile is meaningless for them and a banner about one is just noise.
    if (!isAuthenticated || user?.role === 'vendor' || user?.role === 'admin') {
      setState({ complete: true, profile: null, loading: false, missing: [] });
      return;
    }
    try {
      const res = await api.get('/auth/seller-profile');
      const profile = res.data?.profile || res.data || {};
      const missing = REQUIRED.filter(([k]) => !String(profile[k] || '').trim()).map(([, label]) => label);
      setState({
        // The server computes this too; trusting its answer keeps one
        // definition of "complete" rather than two that can drift.
        complete: res.data?.isComplete ?? missing.length === 0,
        profile,
        loading: false,
        missing
      });
    } catch {
      // A failed check must not block the app. Treated as complete so nothing
      // is gated by a network error — the server enforces it regardless.
      setState({ complete: true, profile: null, loading: false, missing: [] });
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ComplianceContext.Provider value={{ ...state, refresh }}>
      {children}
    </ComplianceContext.Provider>
  );
}

export const useCompliance = () => useContext(ComplianceContext);
